import { zodResolver } from '@hookform/resolvers/zod';
import {
  avatarFileSchema,
  bicSchema,
  frenchDepartmentCodeSchema,
  ibanSchema,
  pictureSchema,
  refineBicMatchesIban,
  signatureUrlSchema,
} from '@omecare/utils';
import {
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { z } from 'zod';
import { installableIds, thesisIds } from '@/utils/doctor-substitute';
import { addRhythmRefinements, rhythmSchemaFields } from '../rhythm-fields';
import { optionalSignatureFileSchema } from '../signature/signature-file.schema';
import { DoctorProfileFormMode, isFieldVisible } from './types';

/**
 * Phone number schema with French validation and formatting
 */
const phoneNumberSchema = z
  .string({ message: 'form.errors.required' })
  .min(1, { message: 'form.errors.required' })
  .refine((value) => isValidPhoneNumber(value, 'FR'), {
    message: 'form.errors.invalid-phone-number',
  })
  .transform((value) => {
    try {
      const phoneNumber = parsePhoneNumberWithError(value, 'FR');
      return phoneNumber.formatNational();
    } catch {
      return value;
    }
  });

/**
 * Address schema for nested address object
 */
const addressSchema = z.object({
  city: z.string({ message: 'form.errors.required' }).min(1, {
    message: 'form.errors.required',
  }),
  line1: z.string({ message: 'form.errors.required' }).min(1, {
    message: 'form.errors.required',
  }),
  line2: z.string().optional(),
  postalCode: z.string({ message: 'form.errors.required' }).min(1, {
    message: 'form.errors.required',
  }),
});

/**
 * Helper to check if address is effectively empty (all required fields blank)
 * Used to implement all-or-nothing address validation:
 * - All fields empty → no address (null)
 * - Any field filled → all required fields must be filled
 */
const isEmptyAddress = (addr: unknown): boolean => {
  if (!addr || typeof addr !== 'object') return true;
  const a = addr as Record<string, string>;
  return !a.city?.trim() && !a.line1?.trim() && !a.postalCode?.trim();
};

/**
 * Address schema with preprocessing for optional address fields
 * Converts empty address objects to null before validation
 * This allows forms to submit with empty address fields without validation errors
 */
const optionalAddressSchema = z.preprocess(
  (val) => (isEmptyAddress(val) ? null : val),
  addressSchema.nullable().optional(),
);

/** Substitute priority bounds (1 = highest, 4 = lowest). */
const PRIORITY_MIN = 1;
const PRIORITY_MAX = 4;

/**
 * Base schema with all possible fields
 * Mode-specific validation is applied via refinements
 */
const baseDoctorProfileSchema = z.object({
  // Identity fields
  firstname: z.string({ message: 'form.errors.required' }).min(1, {
    message: 'form.errors.required',
  }),
  lastname: z.string({ message: 'form.errors.required' }).min(1, {
    message: 'form.errors.required',
  }),
  phoneNumber: phoneNumberSchema,

  // Personal info (optional based on mode)
  dateOfBirth: z
    .date({ message: 'form.errors.required' })
    .nullable()
    .optional(),
  address: optionalAddressSchema,

  // Professional info
  orderRegistrationNumber: z
    .string({ message: 'form.errors.required' })
    .min(1, {
      message: 'form.errors.required',
    }),
  rppsNumber: z.string().nullable().optional(),
  thesis: z
    .enum(thesisIds, { message: 'form.errors.required' })
    .nullable()
    .optional(),
  installable: z
    .enum(installableIds, { message: 'form.errors.required' })
    .nullable()
    .optional(),

  // Rhythm fields (shared)
  ...rhythmSchemaFields,

  // Banking
  iban: ibanSchema,
  bic: bicSchema,

  // Avatar
  file: avatarFileSchema,
  picture: pictureSchema,

  // Signature
  signatureFile: optionalSignatureFileSchema,
  signatureUrl: signatureUrlSchema,

  // Commission (attending-admin only)
  commissionRate: z.coerce.number().nullable().optional(),

  // Doctolib ID (admin modes)
  doctolibId: z.string().nullable().optional(),

  // Priority (substitute-admin only)
  priority: z
    .number()
    .int()
    .min(PRIORITY_MIN)
    .max(PRIORITY_MAX)
    .nullable()
    .optional(),

  // Contract profile (substitute modes, issue #796). Always optional:
  // existing profiles are never blocked.
  urssafNumber: z.string().nullable().optional(),
  licenceNumber: z.string().nullable().optional(),
  licenceExpiresAt: z.date().nullable().optional(),
  licenceCouncilDepartmentCode: frenchDepartmentCodeSchema
    .nullable()
    .optional(),
});

/**
 * Type for form data
 */
type FormData = z.infer<typeof baseDoctorProfileSchema>;

/**
 * Refinement check functions for each field group
 */
const refinementChecks = {
  dateOfBirth: (data: FormData) =>
    data.dateOfBirth !== null && data.dateOfBirth !== undefined,
  address: (data: FormData) =>
    data.address !== null && data.address !== undefined,
  rpps: (data: FormData) =>
    data.rppsNumber !== null &&
    data.rppsNumber !== undefined &&
    data.rppsNumber.length > 0,
  thesis: (data: FormData) => data.thesis !== null && data.thesis !== undefined,
  signature: (data: FormData) => !!(data.signatureFile || data.signatureUrl),
  commission: (data: FormData) => {
    const rate = data.commissionRate;
    return (
      rate !== null &&
      rate !== undefined &&
      !Number.isNaN(Number(rate)) &&
      rate > 0
    );
  },
};

/**
 * Helper to check if mode is for attending doctors
 */
const isAttendingMode = (mode: DoctorProfileFormMode): boolean =>
  mode === 'attending-profile' || mode === 'attending-admin';

/**
 * Creates a mode-specific schema with appropriate validations
 * Applies refinements based on field visibility for the given mode
 *
 * Note: For attending doctors, dateOfBirth and address are visible but optional
 */
export const createDoctorProfileSchema = (
  mode: DoctorProfileFormMode,
  options?: { skipValidation?: boolean },
): z.ZodTypeAny => {
  // Rhythm P >= 2V rule only applies to substitute doctors; attending doctors
  // (and admins editing them) can set any rhythm values.
  let schema: z.ZodTypeAny = addRhythmRefinements(
    baseDoctorProfileSchema.superRefine(refineBicMatchesIban),
    { skipValidation: options?.skipValidation || isAttendingMode(mode) },
  );

  // Apply mode-specific refinements
  // For attending doctors, dateOfBirth is visible but optional
  if (isFieldVisible(mode, 'dateOfBirth') && !isAttendingMode(mode)) {
    schema = schema.refine(refinementChecks.dateOfBirth, {
      message: 'form.errors.required',
      path: ['dateOfBirth'],
    });
  }

  // For attending doctors, address is visible but optional
  if (isFieldVisible(mode, 'address') && !isAttendingMode(mode)) {
    schema = schema.refine(refinementChecks.address, {
      message: 'form.errors.required',
      path: ['address'],
    });
  }

  if (isFieldVisible(mode, 'rpps')) {
    schema = schema.refine(refinementChecks.rpps, {
      message: 'form.errors.required',
      path: ['rppsNumber'],
    });
  }

  if (isFieldVisible(mode, 'thesis')) {
    schema = schema.refine(refinementChecks.thesis, {
      message: 'form.errors.required',
      path: ['thesis'],
    });
  }

  if (isFieldVisible(mode, 'signature')) {
    schema = schema.refine(refinementChecks.signature, {
      message: 'form.errors.signature-required',
      path: ['signatureFile'],
    });
  }

  if (isFieldVisible(mode, 'commission')) {
    schema = schema.refine(refinementChecks.commission, {
      message: 'form.errors.required',
      path: ['commissionRate'],
    });
  }

  return schema;
};

/**
 * Type for the unified form values
 */
export type DoctorProfileFormValues = z.infer<typeof baseDoctorProfileSchema>;

/**
 * Creates a resolver for the specified mode
 */
export const createDoctorProfileFormResolver = (
  mode: DoctorProfileFormMode,
  options?: { skipValidation?: boolean },
) => zodResolver(createDoctorProfileSchema(mode, options));

/**
 * Default values for the form
 */
export const getDefaultDoctorProfileValues = (
  mode: DoctorProfileFormMode,
): Partial<DoctorProfileFormValues> => ({
  firstname: '',
  lastname: '',
  phoneNumber: '',
  dateOfBirth: isFieldVisible(mode, 'dateOfBirth') ? undefined : null,
  address: isFieldVisible(mode, 'address')
    ? { city: '', line1: '', line2: '', postalCode: '' }
    : null,
  orderRegistrationNumber: '',
  rppsNumber: isFieldVisible(mode, 'rpps') ? '' : null,
  thesis: isFieldVisible(mode, 'thesis') ? undefined : null,
  installable: isFieldVisible(mode, 'installable') ? undefined : null,
  onSiteSlotsWithoutAssistant: null,
  remoteSlotsWithoutAssistant: null,
  onSiteSlotsWithAssistant: null,
  remoteSlotsWithAssistant: null,
  rhythmNote: null,
  iban: '',
  bic: '',
  file: undefined,
  picture: '',
  signatureFile: null,
  signatureUrl: '',
  commissionRate: isFieldVisible(mode, 'commission') ? undefined : null,
  doctolibId: isFieldVisible(mode, 'doctolibId') ? '' : null,
  priority: null,
  urssafNumber: isFieldVisible(mode, 'contractProfile') ? '' : null,
  licenceNumber: isFieldVisible(mode, 'contractProfile') ? '' : null,
  licenceExpiresAt: null,
  licenceCouncilDepartmentCode: null,
});
